// assets/eos-kisisellestirme.js
import { Component } from '@theme/component';

/** Turkish letters plus the Latin ones; digits and symbols cannot be engraved here. */
const LETTERS = 'A-Za-zÇĞİÖŞÜçğıöşü';
const NOT_LETTER = new RegExp(`[^${LETTERS}]`, 'g');
const NOT_NAME = new RegExp(`[^${LETTERS} ]`, 'g');

/** Quiet time after the last edit before a complete value counts as "seen". */
const PREVIEW_SETTLE_MS = 900;

/**
 * Fire-and-forget custom analytics event. Never throws, never blocks: a
 * missing or failing Shopify analytics bus must not affect the form.
 * @param {string} name
 * @param {Record<string, unknown>} data
 */
const publish = (name, data) => {
  try {
    /** @type {any} */ (window).Shopify?.analytics?.publish?.(name, data);
  } catch (_) {
    /* analytics is optional */
  }
};

/**
 * Product personalisation driven by the product's `eos.kisisellestirme` type.
 *
 * Every field is a real form control bound to the product form with `form=`,
 * so the values travel as line item properties and native `required`
 * validation is the hard gate: the browser never fires `submit` for an
 * incomplete form, so nothing reaches the cart unpersonalised.
 *
 * This component sanitises input, keeps the hidden `_Onizleme` record in step
 * for the cart, and announces completeness for the sticky bar. It does NOT
 * paint the preview: the `<eos-onizleme>` stages pull their own state, which is
 * the only arrangement that survives Horizon replacing the media gallery and
 * the preview module loading late.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} [girdiler]
 * @property {HTMLElement[]} [sayaclar]
 * @property {HTMLInputElement} [kimlik]
 * @property {HTMLElement} [uyari]
 * @extends Component<Refs>
 */
class EosKisisellestirme extends Component {
  /** Complete values already reported, so each distinct value is sent once. */
  #reportedPreviews = new Set();
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #previewTimer;

  get #config() {
    return this.querySelector('.eos-kis')?.dataset ?? this.dataset;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#prefillFromUrl();
    this.#kutulariDoldur();
    this.#render();

    // An invalid submit attempt fires `invalid` on each bad field before the
    // browser shows its bubble; surface the inline hint at the same moment.
    this.addEventListener('invalid', this.#onInvalid, true);

    // Two-letter boxes ("iki-harf"). Delegated on the component so a re-render
    // of the block's inner markup cannot leave the boxes without listeners.
    this.addEventListener('input', this.#onKutuInput);
    this.addEventListener('keydown', this.#onKutuKeydown);
    this.addEventListener('paste', this.#onKutuPaste);
    this.addEventListener('focusin', this.#onFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('invalid', this.#onInvalid, true);
    this.removeEventListener('input', this.#onKutuInput);
    this.removeEventListener('keydown', this.#onKutuKeydown);
    this.removeEventListener('paste', this.#onKutuPaste);
    this.removeEventListener('focusin', this.#onFocusIn);
    clearTimeout(this.#previewTimer);
  }

  /** @returns {HTMLInputElement[]} */
  get #inputs() {
    return this.refs.girdiler ?? [];
  }

  /** @returns {boolean} Whether every personalisation field is complete. */
  get tamam() {
    // `validity.valid`, not checkValidity(): the latter fires `invalid`, which
    // would raise the warning before the shopper has tried to buy.
    return this.#inputs.every((input) => input.validity.valid);
  }

  /** Scrolls to and focuses the first incomplete field (used by the sticky bar). */
  odaklan() {
    const invalid = this.#inputs.find((input) => !input.validity.valid);
    let target = invalid ?? this.#inputs[0];
    if (!target) return;
    // The two-letter value lives in a visually hidden field; send the shopper
    // to the box that still needs a letter instead.
    if (target.hasAttribute('data-kutulu')) target = this.#bosKutu() ?? target;
    const focusable = target.type === 'radio' ? target.closest('fieldset') ?? target : target;
    focusable.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.focus({ preventScroll: true });
    if (this.refs.uyari) this.refs.uyari.hidden = false;
  }

  /** @param {Event} event */
  girdiDegisti(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (input.type !== 'radio') this.#sanitize(input);
    this.#render();
  }

  #onInvalid = () => {
    if (this.refs.uyari) this.refs.uyari.hidden = false;
  };

  /* --- İKİ HARF KUTULARI ---------------------------------------------------
   * Each box holds one letter; the line item property is the hidden
   * `[data-kutulu]` field ("EŞ"), which keeps every reader (sticky bar, wizard,
   * preview, gift share link, cart) unchanged. Boxes -> field on every edit;
   * field -> boxes when someone else writes the field (URL prefill, wizard).
   */

  /** @returns {HTMLInputElement[]} */
  get #kutular() {
    return /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input.eos-kis__kutu')]);
  }

  /** @returns {HTMLInputElement | null} */
  get #kutuluAlan() {
    return this.querySelector('input[data-kutulu]');
  }

  /** @returns {HTMLInputElement | undefined} First empty box, else the last one. */
  #bosKutu() {
    const kutular = this.#kutular;
    return kutular.find((kutu) => !kutu.value) ?? kutular[kutular.length - 1];
  }

  /**
   * @param {string} text
   * @returns {string[]} Letters only, upper-cased the Turkish way.
   */
  #harfler(text) {
    return Array.from(String(text || '').replace(NOT_LETTER, '').toLocaleUpperCase('tr-TR'));
  }

  /** Copies the combined value into the boxes (after prefill and external writes). */
  #kutulariDoldur() {
    const alan = this.#kutuluAlan;
    if (!alan) return;
    const harfler = this.#harfler(alan.value);
    this.#kutular.forEach((kutu, i) => {
      const harf = harfler[i] ?? '';
      if (kutu.value !== harf) kutu.value = harf;
    });
  }

  /** Writes the boxes into the combined field and refreshes everything that reads it. */
  #kutulariYaz() {
    const alan = this.#kutuluAlan;
    if (!alan) return;
    alan.value = this.#kutular.map((kutu) => kutu.value).join('');
    this.#sanitize(alan);
    this.#render();
  }

  /**
   * Fills boxes from `start` with `harfler`, then focuses the box after the
   * last one written (or the last box).
   * @param {number} start
   * @param {string[]} harfler
   */
  #dagit(start, harfler) {
    const kutular = this.#kutular;
    let i = start;
    for (const harf of harfler) {
      if (i >= kutular.length) break;
      kutular[i].value = harf;
      i += 1;
    }
    this.#kutulariYaz();
    const sonraki = kutular[Math.min(i, kutular.length - 1)];
    if (!sonraki) return;
    if (sonraki !== document.activeElement) sonraki.focus();
    else sonraki.select();
  }

  /** @param {Event} event */
  #onKutuInput = (event) => {
    const hedef = /** @type {HTMLInputElement} */ (event.target);
    if (hedef.matches?.('input[data-kutulu]')) {
      // Someone other than the boxes wrote the field (the wizard mirrors it).
      // After the delegated sanitiser has run, so the boxes get the clean value.
      setTimeout(() => this.#kutulariDoldur(), 0);
      return;
    }
    if (!hedef.classList?.contains('eos-kis__kutu')) return;

    const index = Number(hedef.dataset.kutu) || 0;
    const harfler = this.#harfler(hedef.value);
    if (harfler.length > 1) {
      // Autocomplete or an IME can push several characters past maxlength.
      this.#dagit(index, harfler);
      return;
    }
    hedef.value = harfler[0] ?? '';
    this.#kutulariYaz();
    if (hedef.value) this.#kutular[index + 1]?.focus();
  };

  /** @param {KeyboardEvent} event */
  #onKutuKeydown = (event) => {
    const hedef = /** @type {HTMLInputElement} */ (event.target);
    if (!hedef.classList?.contains('eos-kis__kutu')) return;
    const kutular = this.#kutular;
    const index = Number(hedef.dataset.kutu) || 0;
    const onceki = kutular[index - 1];
    const sonraki = kutular[index + 1];

    if (event.key === 'Backspace' && !hedef.value && onceki) {
      event.preventDefault();
      onceki.focus();
      onceki.select();
    } else if (event.key === 'ArrowLeft' && onceki && (hedef.selectionStart ?? 0) === 0) {
      event.preventDefault();
      onceki.focus();
    } else if (event.key === 'ArrowRight' && sonraki && (hedef.selectionStart ?? 0) >= hedef.value.length) {
      event.preventDefault();
      sonraki.focus();
    } else if (event.key.length === 1 && hedef.value && hedef.selectionStart === hedef.selectionEnd) {
      // A full box with the caret inside: typing replaces the letter instead of
      // being swallowed by maxlength="1".
      hedef.select();
    }
  };

  /** @param {ClipboardEvent} event */
  #onKutuPaste = (event) => {
    const hedef = /** @type {HTMLInputElement} */ (event.target);
    if (!hedef.classList?.contains('eos-kis__kutu')) return;
    const harfler = this.#harfler(event.clipboardData?.getData('text') ?? '');
    if (!harfler.length) return;
    event.preventDefault();
    this.#dagit(Number(hedef.dataset.kutu) || 0, harfler);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const hedef = /** @type {HTMLInputElement} */ (event.target);
    if (hedef.matches?.('input[data-kutulu]')) {
      // Browser validation, the sticky bar or a "design it" link focused the
      // hidden field: the shopper types into the boxes.
      this.#bosKutu()?.focus();
      return;
    }
    // Selected on focus, so the next keystroke replaces the letter.
    if (hedef.classList?.contains('eos-kis__kutu') && hedef.value) hedef.select();
  };

  /**
   * Values handed over in the URL: the home-page demo sends `?harf=EŞ`, the
   * "complete the set" links in the cart and on the product page send what
   * the shopper already chose for the other piece (`harf`, `isim`, `isim2`,
   * `burc` as a zodiac code). A field that already holds a value is left
   * alone, and every value still passes the sanitiser and `required` checks,
   * so a link can only suggest - it can never smuggle an invalid value in.
   */
  #prefillFromUrl() {
    const params = new URLSearchParams(location.search);
    /** @type {[string, string, number][]} */
    const map = [
      ['harf', 'harf', 0],
      ['isim', 'isim', 0],
      ['isim2', 'isim', 1],
      ['burc', 'burc', 0],
      ['burc2', 'burc', 1],
    ];

    for (const [param, tur, bagli] of map) {
      const value = params.get(param);
      if (!value) continue;
      const fields = this.#inputs.filter(
        (input) => input.dataset.tur === tur && (Number(input.dataset.bagli) || 0) === bagli
      );

      if (tur === 'burc') {
        if (fields.some((radio) => radio.checked)) continue;
        const radio = fields.find((field) => field.dataset.kod === value);
        if (radio) radio.checked = true;
        continue;
      }

      const field = fields[0];
      if (!field || field.value) continue;
      field.value = value;
      this.#sanitize(field);
    }
  }

  /** @param {HTMLInputElement} input */
  #sanitize(input) {
    const before = input.value;
    const isName = input.dataset.tur === 'isim';
    let next = before.replace(isName ? NOT_NAME : NOT_LETTER, '');

    if (isName) {
      // One space between words, none leading: "Ayşe Nur", not "  Ayşe   Nur".
      next = next.replace(/\s{2,}/g, ' ').replace(/^\s+/, '');
    } else {
      next = next.toLocaleUpperCase('tr-TR');
    }

    // maxlength governs typing only; a paste or a prefilled URL can exceed it.
    if (input.maxLength > 0 && next.length > input.maxLength) next = next.slice(0, input.maxLength);

    if (next !== before) {
      const caret = input.selectionStart;
      input.value = next;
      if (caret !== null && document.activeElement === input) {
        const position = Math.max(0, caret - (before.length - next.length));
        input.setSelectionRange(position, position);
      }
    }

    // `minlength` is only enforced after a user edit; a prefilled value would
    // slip through, so the rule is restated as a custom validity.
    const trimmed = next.trim();
    const tooShort = trimmed.length > 0 && trimmed.length < input.minLength;
    input.setCustomValidity(tooShort ? this.#config.hata || ' ' : '');
  }

  /**
   * Reads every field once and publishes the result.
   *
   * The drawing is NOT painted here. Both preview stages (`<eos-onizleme>` in
   * the media column and the 72px row above these fields) pull their own state
   * from the DOM, because Horizon replaces the media gallery on every variant
   * change and the preview module loads late. Pushing from here would mean
   * repainting a node that may not exist yet, or may have just been replaced.
   *
   * @returns {void}
   */
  #render() {
    const inputs = this.#inputs;

    for (const counter of this.refs.sayaclar ?? []) {
      const field = inputs.find(
        (input) => input.type !== 'radio' && input.dataset.bagli === counter.dataset.bagli
      );
      if (field) counter.textContent = `${field.value.length}/${field.maxLength}`;
    }

    // Dot strip under the multi-letter field: one filled dot per letter.
    const izler = this.querySelectorAll('.eos-kis__harf-izi .eos-kis__iz');
    if (izler.length) {
      const alan = inputs.find((input) => input.dataset.tur === 'harf');
      const adet = alan ? Array.from(alan.value).length : 0;
      izler.forEach((iz, i) => iz.toggleAttribute('data-dolu', i < adet));
    }

    this.#writeMachineProperty();

    const complete = this.tamam;
    if (complete && this.refs.uyari) this.refs.uyari.hidden = true;
    this.#schedulePreviewEvent(complete);

    this.dispatchEvent(
      new CustomEvent('eos:kisisellestirme:change', {
        bubbles: true,
        detail: {
          tamam: complete,
          ozet: this.#summary(),
          // The sticky bar only needs `tamam`/`ozet`; `tip` and `degerler` are
          // for anything that wants the values without parsing a human string.
          tip: this.#config.tip ?? '',
          degerler: this.#values(),
        },
      })
    );
  }

  /**
   * `eos_onizleme_goruntulendi`: the preview now shows a complete design.
   *
   * Only products that actually print a preview stage (`<eos-onizleme>` in this
   * product's section) report it - on types without a drawing nothing is
   * "shown". The event waits for a short pause so typing a name does not report
   * every intermediate spelling, and each distinct value is reported once.
   * The payload carries the product and type, never the typed text.
   *
   * @param {boolean} complete
   */
  #schedulePreviewEvent(complete) {
    clearTimeout(this.#previewTimer);
    if (!complete) return;
    const key = JSON.stringify(this.#values());
    if (this.#reportedPreviews.has(key)) return;

    this.#previewTimer = setTimeout(() => {
      if (!this.isConnected || !this.tamam) return;
      if (JSON.stringify(this.#values()) !== key || this.#reportedPreviews.has(key)) return;
      const scope = this.closest('.shopify-section') ?? document;
      if (!scope.querySelector('eos-onizleme')) return;
      this.#reportedPreviews.add(key);
      publish('eos_onizleme_goruntulendi', {
        urun: this.#config.urun ?? '',
        tip: this.#config.tip ?? '',
      });
    }, PREVIEW_SETTLE_MS);
  }

  /**
   * @returns {{tur: string, bagli: number, deger: string}[]} Structured values,
   * language-independent: zodiac entries carry the code (`koc`), not "Koç".
   */
  #values() {
    /** @type {{tur: string, bagli: number, deger: string}[]} */
    const out = [];
    for (const input of this.#inputs) {
      if (input.type === 'radio' && !input.checked) continue;
      out.push({
        tur: input.dataset.tur ?? '',
        bagli: Number(input.dataset.bagli) || 0,
        deger: input.type === 'radio' ? input.dataset.kod ?? '' : input.value.trim(),
      });
    }
    return out;
  }

  /**
   * Keeps the hidden `_Onizleme` record in step so the cart can draw the same
   * piece without JavaScript. Format and precedence: see
   * `snippets/eos-onizleme-kimlik.liquid`.
   *
   * Finish, chain and family are read from the preview element rather than
   * recomputed here — one reader of the variant picker, not two that can drift.
   * If the element has not been upgraded yet, the server-rendered values stay
   * and only the text is refreshed.
   */
  #writeMachineProperty() {
    const field = this.refs.kimlik;
    if (!field) return;

    const parts = String(field.value || 'v1|||||||').split('|');
    while (parts.length < 8) parts.push('');

    // Scoped to this product's section: a recommendation carousel or a quick-add
    // dialog can put another product's <eos-onizleme> earlier in the document,
    // and reading that one would stamp the wrong type onto this line item.
    const kapsam = this.closest('.shopify-section') ?? document;
    const stage = /** @type {any} */ (kapsam.querySelector('eos-onizleme'));
    const options = stage?.secenekler;
    if (options) {
      parts[1] = options.tip || parts[1];
      parts[2] = options.urun || parts[2];
      parts[3] = options.varyant || parts[3];
      parts[4] = options.renk || parts[4];
      parts[5] = options.zincirCm ? String(options.zincirCm) : '';
    }

    let first = '';
    let second = '';
    for (const value of this.#values()) {
      if (value.tur === 'harf') first = value.deger;
      else if (value.bagli) second = value.deger;
      else first = value.deger;
    }
    // The sanitiser cannot produce a pipe, but this value is posted to a public
    // endpoint and read back as a record: never let a separator through.
    parts[6] = first.replace(/\|/g, ' ');
    parts[7] = second.replace(/\|/g, ' ');

    const next = parts.slice(0, 8).join('|');
    if (next !== field.value) field.value = next;
  }

  /** @returns {string} Short human summary, e.g. "EŞ" or "Koç & Aslan". */
  #summary() {
    const parts = [];
    const seen = new Set();
    for (const input of this.#inputs) {
      if (input.type === 'radio') {
        if (input.checked) parts.push(input.value);
      } else if (!seen.has(input) && input.value.trim()) {
        parts.push(input.value.trim());
      }
      seen.add(input);
    }
    return parts.join(' & ');
  }
}

if (!customElements.get('eos-kisisellestirme-component')) {
  customElements.define('eos-kisisellestirme-component', EosKisisellestirme);
}
